import * as React from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { productsQueryOptions } from '@/web/lib/queryOptions';
import { useMutation, useQueryClient, useQuery, keepPreviousData } from '@tanstack/react-query';
import { ProductsList } from '@/web/components/molecules/ProductsList';
import type { Product } from '@/types';
import { ProductFormModal } from '@/web/components/molecules/ProductFormModal';
import { updateProduct, deleteProduct } from '@/web/lib/services';
import { PaginationNav } from '@/web/components/molecules/Pagination';

export type ProductBrowseParameters = {
  pageNo?: number,
  subcategory?: string,
}

export const Route = createFileRoute('/products/$categoryId/$subcategoryId')({
  loaderDeps: ({ search: { pageNo } }) => ({ pageNo }),
  loader: async ({ params, context, deps: { pageNo } }) => {
    const pageNoWithDefault = pageNo ?? 1;
    return context.queryClient?.ensureQueryData(productsQueryOptions(params.categoryId, params.subcategoryId, pageNoWithDefault));
  },
  validateSearch: (search: Record<string, unknown>): ProductBrowseParameters => {
    let queryParams: ProductBrowseParameters = {};
    if (search.pageNo) {
      queryParams.pageNo = Number(search?.pageNo);
    }
    return queryParams;
  },
  preload: true,
  component: SubcategoryProducts,
});

export function SubcategoryProducts() {
  const params = useParams({ from: '/products/$categoryId/$subcategoryId' });
  const queryParams = Route.useSearch();
  const pageNo = queryParams.pageNo || 1
  const mode = 'edit';
  const [isLoading, setIsLoading] = React.useState(false);
  const [product, setProduct] = React.useState<Product | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const editProduct = (product: Product) => () => {
    setProduct(product);
    setIsOpen(true);
  }

  const updateProductMutation = useMutation({
    mutationFn: updateProduct,
    onSuccess: () => {
      queryClient.invalidateQueries(productsQueryOptions(params.categoryId, params.subcategoryId, pageNo))
      setIsOpen(false);
    }
  });

  const deleteProductMutation = useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries(productsQueryOptions(params.categoryId, params.subcategoryId, pageNo))
      setIsOpen(false);
    }
  });

  const handleDelete = (productId: string) => {
    deleteProductMutation.mutate(productId);
  }

  const handleSubmit = (product: Product) => {
    updateProductMutation.mutate(product);
  }

  let subcategoryId: string | null = params.subcategoryId;
  if(subcategoryId === 'all') {
    subcategoryId = null;
  }
  const { data, isFetching, isPlaceholderData } = useQuery({
    ...productsQueryOptions(params.categoryId, subcategoryId, pageNo),
    placeholderData: keepPreviousData,
  });

  const products = data?.products || [];
  const pages = data?.pages || [];
  const totalCount = data?.totalCount || 0;

  return (
    <div>
      <ProductFormModal
        isOpen={isOpen}
        isLoading={isLoading}
        mode={mode}
        onDelete={handleDelete}
        onSubmit={handleSubmit}
        onOpenChange={setIsOpen}
        product={product}
      />
      {data && (
        <PaginationNav
          subcategoryId={params.subcategoryId}
          categoryId={params.categoryId}
          pages={pages}
          pageNo={pageNo}
          totalCount={totalCount}
        />
      )}
      <ProductsList
        handleEditProduct={editProduct}
        products={products}
        isLoading={isFetching && (isPlaceholderData || !data)}
      />
    </div>
  );
}
